import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {CaretDown, Check, Users, Warning} from '../icons';
import {Spinner} from '../Spinner';
import {clientAvatarColor, nameInitials} from '../elements/ClientAvatar';
import {LocalStorage} from '../../utils/LocalStorage';
import {isOk} from '../../utils/apiResponse';
import {AlertRecipientsApi, recipientsPayload} from '../../api/alertRecipientsApi';
import {
    canHaveRecipients,
    lineRecipientKeys,
    mailSummaryOf,
    memberName,
    myRecipientState,
    recipientLabelKey,
    recipientToggleSlug,
    recipientsFor,
    recipientsKeyOf,
    withMyRecipient,
} from './alertRecipientsModel';
import './AlertRecipients.scss';

const RAW = {interpolation: {escapeValue: false}};

// «Mis alertas» de quien mira: la misma ruta que `myAlertsPath` del Centro (`alerts_center/alertsCenterPaths`).
// No se importa de ahí porque la configuración de alertas de la línea, que es del clásico, monta este archivo
// y no puede arrastrar nada de `alerts_center/` (`classicIsolation.test.js`).
function myAlertsUrl(userId) {
    return userId ? `/user/${encodeURIComponent(userId)}` : '/user-configuration';
}

/**
 * «Cada persona lo activa en Mis alertas.» con el link a «Mis alertas» de quien mira (review C-05): el panel
 * no ofrece «Desactivar», así que es por ahí que se apaga.
 */
function RecipientsHint({className, myUserId}) {
    const {t} = useTranslation();
    return (
        <p className={className}>
            {t('alerts_recipients.hint')}{' '}
            <Link className="alr-link" to={myAlertsUrl(myUserId)}>{t('alerts_recipients.hint_link')}</Link>
        </p>
    );
}

/**
 * «Quién recibe esta alerta por mail» (tanda «Control de líneas», 15/10; caso Kendal: la alerta de
 * ritmo le llegaba a 1 de 3 personas y nadie lo sabía).
 *
 * El dato es `GET agency-client/<id>/alert-recipients/`. Si no viene con la forma del contrato (BE viejo,
 * 404 uniforme, red) no se dibuja nada: no se deduce nada de un 404.
 *
 * `enabled` deja pedirlo recién cuando hace falta (el renglón de «Ahora» lo pide al desplegarse).
 * «Activar» prende el MISMO interruptor de «Mis alertas», optimista y con vuelta atrás si no se guardó.
 */
export function useAlertRecipients(clientId, {enabled = true} = {}) {
    const {t} = useTranslation();
    const [state, setState] = useState({status: 'idle', payload: null});
    const [attempt, setAttempt] = useState(0);
    const [savingKey, setSavingKey] = useState(null);
    const myUserId = LocalStorage.fetchUserId() || null;
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (!enabled || clientId == null) return undefined;
        let current = true;
        setState({status: 'loading', payload: null});
        AlertRecipientsApi.get(clientId).then((response) => {
            if (!current) return;
            const payload = recipientsPayload(response);
            setState(payload ? {status: 'ready', payload} : {status: 'unavailable', payload: null});
        });
        // Una respuesta de otro cliente (la ruta no remonta) no pisa la del actual.
        return () => { current = false; };
    }, [clientId, enabled, attempt]);

    // «Reintentar» del panel cuando la carga falló: vuelve a pedir, con el spinner.
    const retry = useCallback(() => setAttempt((value) => value + 1), []);

    const activate = useCallback((key) => {
        const slug = recipientToggleSlug(key);
        if (!slug || myUserId == null) return;
        setState((current) => ({...current, payload: withMyRecipient(current.payload, key, myUserId, true)}));
        setSavingKey(key);
        AlertRecipientsApi.setEmailAlert(myUserId, slug, true).then((response) => {
            if (!mounted.current) return;
            setSavingKey(null);
            if (isOk(response)) return;
            setState((current) => ({...current, payload: withMyRecipient(current.payload, key, myUserId, false)}));
            toast.error(t('user_email_alerts.save_error'));
        });
        // `t` cambia de identidad entre renders de i18next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myUserId]);

    return {status: state.status, payload: state.payload, myUserId, savingKey, activate, retry};
}

function PersonChip({member}) {
    const name = memberName(member);
    return (
        <li className="alr-person">
            <span className="alr-avatar" aria-hidden="true" style={{backgroundColor: clientAvatarColor(member.id)}}>
                {nameInitials(name)}
            </span>
            <span className="alr-person__name">{name}</span>
        </li>
    );
}

function MyState({mine, canActivate, saving, onActivate}) {
    const {t} = useTranslation();
    if (!mine) return null;
    if (mine.receives) {
        return (
            <span className="alr-me -on" data-testid="alr-me">
                <Check size={13} weight="bold" aria-hidden="true"/>
                {t('alerts_recipients.me_on')}
            </span>
        );
    }
    return (
        <span className="alr-me -off" data-testid="alr-me">
            {t('alerts_recipients.me_off')}
            {canActivate && (
                <>
                    <span aria-hidden="true">·</span>
                    <button type="button" className="alr-me__action" disabled={saving} aria-busy={saving || undefined}
                            onClick={onActivate}>
                        {t('alerts_recipients.me_activate')}
                    </button>
                </>
            )}
        </span>
    );
}

/**
 * Un aviso: quiénes lo tienen prendido (o el aviso de que no lo tiene nadie) y el estado de quien mira.
 * `showLabel` lo nombra (la caja de la línea lista varios); en la tarjeta de una alerta ya se sabe cuál es.
 */
export function AlertRecipientsRow({alertKey, recipients, showLabel = false}) {
    const {t} = useTranslation();
    const {payload, myUserId, savingKey, activate} = recipients;
    const people = recipientsFor(payload, alertKey);
    const mine = myRecipientState(payload, alertKey, myUserId);
    const labelKey = recipientLabelKey(alertKey);
    return (
        <div className="alr-row" data-testid="alr-row" data-alert={alertKey}>
            {showLabel && labelKey && <p className="alr-row__label">{t(labelKey)}</p>}
            <div className="alr-row__body">
                {people.length > 0 ? (
                    <ul className="alr-people" aria-label={t('alerts_recipients.count', {count: people.length})}>
                        {people.map((member) => <PersonChip key={member.id} member={member}/>)}
                    </ul>
                ) : (
                    <p className="alr-nobody" data-testid="alr-nobody">
                        <Warning size={14} weight="bold" aria-hidden="true"/>
                        {t('alerts_recipients.nobody')}
                    </p>
                )}
                <MyState mine={mine} canActivate={Boolean(recipientToggleSlug(alertKey))}
                         saving={savingKey === alertKey} onActivate={() => activate(alertKey)}/>
            </div>
        </div>
    );
}

/**
 * La caja de la configuración de alertas de la línea: una fila por aviso, en el orden de «Mis alertas».
 * Va aparte de las sensibilidades a propósito: se ve aunque la línea use la configuración de la agencia,
 * que es lo de casi todas. `renderFrame(contenido)` lo envuelve en la caja de quien lo monta, así la caja
 * tampoco aparece cuando no hay nada que mostrar.
 */
export function AlertRecipients({clientId, renderFrame = (content) => content}) {
    const recipients = useAlertRecipients(clientId);
    if (recipients.status !== 'ready') return null;
    const keys = lineRecipientKeys(recipients.payload);
    if (!keys.length) return null;
    return renderFrame(
        <div className="alr-box" data-testid="alr-box">
            <RecipientsHint className="alr-box__hint" myUserId={recipients.myUserId}/>
            <ul className="alr-rows">
                {keys.map((key) => (
                    <li key={key} className="alr-rows__item">
                        <AlertRecipientsRow alertKey={key} recipients={recipients} showLabel/>
                    </li>
                ))}
            </ul>
        </div>,
    );
}

function mailText(summary, t) {
    if (summary.kind === 'nobody') return t('alerts_recipients.emailed_none');
    return t('alerts_recipients.emailed', {count: summary.count, ...RAW});
}

/**
 * El renglón de una alerta de «Ahora». Dice dos cosas distintas y no las mezcla:
 * - el renglón es lo que pasó AL DISPARARSE (lo que guardó la Bitácora, `recipient_count` / `no_recipients`):
 *   a cuántas personas se les mandó el mail o que no había a quién mandarlo. No afirma la entrega: eso no
 *   lo sabe Dashbo. Sin el dato (una alerta anterior a la tanda, o un mail que el gate frenó por repetido),
 *   no se dibuja: no se inventa un cero.
 * - el panel desplegado es la configuración de HOY (`alert-recipients`): quién la recibe ahora y, para
 *   quien mira, «Activar» si le corresponde. Puede no coincidir con el renglón, y no tiene por qué.
 */
export function AlertMailLine({clientId, item}) {
    const {t} = useTranslation();
    const panelId = useId();
    const [open, setOpen] = useState(false);
    const [opened, setOpened] = useState(false);
    const recipients = useAlertRecipients(clientId, {enabled: opened});
    const summary = mailSummaryOf(item && item.detail);
    if (!summary) return null;

    const key = recipientsKeyOf(item, recipients.payload && recipients.payload.alerts);
    const label = (
        <>
            <Users size={13} weight="bold" aria-hidden="true"/>
            <span>{mailText(summary, t)}</span>
        </>
    );

    // Una alerta sin interruptor por persona (una regla propia elige sus destinatarios en la regla) queda
    // con el dato, sin un botón que despliega un panel vacío.
    if (!canHaveRecipients(item)) {
        return <p className="alr-mail" data-testid="alr-mail">{label}</p>;
    }

    const toggle = () => {
        setOpened(true);
        setOpen((value) => !value);
    };

    // Si el pedido falla, el botón se queda (antes desaparecía bajo el cursor) y el panel lo dice, con
    // «Reintentar». No se distingue un 404 de un error de red: el 404 es el uniforme de «sin acceso». En los
    // dos casos queda la bajada a «Mis alertas», que es donde se elige qué le llega a cada uno (QA-VIS-ods-03).
    const failed = recipients.status === 'unavailable';
    const noData = recipients.status === 'ready' && !key;

    return (
        <div className={`alr-mail-wrap${open ? ' -open' : ''}`}>
            <button type="button" className="alr-mail" data-testid="alr-mail"
                    aria-expanded={open} aria-controls={panelId} onClick={toggle}>
                {label}
                <CaretDown size={11} weight="bold" aria-hidden="true" className="alr-mail__caret"/>
            </button>
            <div id={panelId} className="alr-panel" hidden={!open}>
                {open && recipients.status === 'loading' && (
                    <div className="alr-panel__loading"><Spinner color="positive small"/></div>
                )}
                {open && (failed || noData) && (
                    <>
                        <div className="alr-panel__error">
                            <p className="alr-panel__hint" data-testid="alr-panel-empty">
                                {t(failed ? 'alerts_recipients.load_error' : 'alerts_recipients.no_data')}
                            </p>
                            {failed && (
                                <button type="button" className="alr-retry" data-testid="alr-panel-retry"
                                        onClick={recipients.retry}>
                                    {t('alerts_recipients.retry')}
                                </button>
                            )}
                        </div>
                        <RecipientsHint className="alr-panel__hint" myUserId={recipients.myUserId}/>
                    </>
                )}
                {open && recipients.status === 'ready' && key && (
                    <>
                        <p className="alr-panel__title">{t('alerts_recipients.title_today')}</p>
                        <AlertRecipientsRow alertKey={key} recipients={recipients}/>
                        <RecipientsHint className="alr-panel__hint" myUserId={recipients.myUserId}/>
                    </>
                )}
            </div>
        </div>
    );
}

export default AlertRecipients;
